/**
 * Goals Tab — main container for the Maqsadlar tab in Home screen.
 * Detects newly achieved goals and triggers celebration + auto feed post.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Target } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useGoalsStore } from '@turnifly/shared/stores'
import { useGoalsByYear } from '@turnifly/shared/hooks'
import { createGoalAchievementPost } from '@turnifly/shared/api'
import { EmptyState } from '@turnifly/shared/ui'
import { YearNavHeader } from './year-nav-header'
import { YearProgressBar } from './year-progress-bar'
import { GoalCard } from './goal-card'
import { GoalCardSkeleton } from './goal-card-skeleton'
import { GoalFab } from './goal-fab'
import { GoalCreateSheet } from './goal-create-sheet'
import { GoalEntrySheet } from './goal-entry-sheet'
import { GoalCelebration } from './goal-celebration'
import { GoalDetailSheet } from './goal-detail-sheet'
import type { Goal } from '@turnifly/shared/types'

export const GoalsTab: React.FC = () => {
  const { t } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id) ?? ''
  const { selectedYear, nextYear, prevYear, openCreate, viewingGoalId, setViewingGoalId } = useGoalsStore()
  const { data: goals, isLoading } = useGoalsByYear()

  // ── Achievement detection ──
  const prevGoalsRef = useRef<Goal[] | null>(null)
  const [celebratingGoal, setCelebratingGoal] = useState<Goal | null>(null)

  useEffect(() => {
    if (!goals || goals.length === 0) {
      prevGoalsRef.current = goals ?? null
      return
    }

    const prev = prevGoalsRef.current
    if (prev) {
      // Find any goal that just became 'achieved' (was not achieved before)
      for (const goal of goals) {
        if (goal.status !== 'achieved') continue
        const prevGoal = prev.find((g) => g.id === goal.id)
        if (prevGoal && prevGoal.status !== 'achieved') {
          // Newly achieved! Trigger celebration
          setCelebratingGoal(goal)
          // Auto-create feed post (fire-and-forget)
          if (userId) {
            createGoalAchievementPost(userId, goal).catch(() => {})
          }
          break
        }
      }
    }

    prevGoalsRef.current = goals
  }, [goals, userId])

  const dismissCelebration = useCallback((): void => {
    setCelebratingGoal(null)
  }, [])

  return (
    <>
      <YearNavHeader
        year={selectedYear}
        onPrev={prevYear}
        onNext={nextYear}
        goalCount={goals?.length ?? 0}
      />

      <YearProgressBar year={selectedYear} />

      {/* Goals list */}
      {isLoading ? (
        <GoalCardSkeleton />
      ) : goals && goals.length > 0 ? (
        <div>
          {goals.map((goal, i) => (
            <GoalCard key={goal.id} goal={goal} index={i} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={
            <div className="w-14 h-14 rounded-2xl bg-primary/[0.08] flex items-center justify-center">
              <Target size={28} className="text-primary" />
            </div>
          }
          title={t('goals.noGoals')}
          description={t('goals.noGoalsDesc')}
          action={
            <button
              onClick={openCreate}
              className="mt-3 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors tap-highlight-transparent"
            >
              {t('goals.newGoal')}
            </button>
          }
        />
      )}

      <GoalFab />
      <GoalCreateSheet />
      <GoalEntrySheet />

      {/* Celebration overlay */}
      <GoalCelebration goal={celebratingGoal} onDismiss={dismissCelebration} />

      {/* Detail sheet */}
      {viewingGoalId && (
        <GoalDetailSheet goalId={viewingGoalId} onClose={() => setViewingGoalId(null)} />
      )}
    </>
  )
}
